import { useCallback } from 'react';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useAppDispatch } from '../useAppDispatch.tsx';
import { api } from '@/services/ApiService.ts';
import { useUser } from '@/hooks/user/useUser.ts';
import { UserModel, UserProfileModel } from '@/apiTypes/UserTypes.ts';

export type UpdateUserProfileFn = (
    changes: Partial<UserProfileModel>,
    userWorkStates?: string[],
) => Promise<{
    data: UserModel | undefined;
    error: FetchBaseQueryError | SerializedError | undefined;
}>;

export const useUpdateUserProfile = (): UpdateUserProfileFn => {
    const dispatch = useAppDispatch();
    // Keeps the user query loaded so the cache read below has a profile.
    useUser();

    return useCallback<UpdateUserProfileFn>(async (changes, userWorkStates) => {
        // Read and patch the cache at call time so a save made before the user reloads builds on this one.
        const cachedProfile = dispatch((_, getState) => api.endpoints.getUser.select()(getState()).data?.userProfile);
        const userProfile = {
            ...cachedProfile,
            ...changes,
            id: cachedProfile?.id || '',
            userId: cachedProfile?.userId || '',
        };
        const patch = dispatch(api.util.updateQueryData('getUser', undefined, (draft) => {
            if (draft.userProfile) {
                Object.assign(draft.userProfile, changes);
            }
        }));
        const promise = dispatch(api.endpoints.updateProfile.initiate(userWorkStates !== undefined ? { userProfile, userWorkStates } : { userProfile }));
        const { data, error } = await promise;
        if (error) {
            patch.undo();
        }
        return { data, error };
    }, [dispatch]);
};
